import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/native';
import { PaddingContainer, Subtitle, Title } from './styledComponents';
import ScrollableView from '@/shared/components/ScrollableView';
import useExploreStore from '@/stores/useExploreStore';
import useStore from '@/stores/useStore';
import SelectSessionType from './BookCalendar/SelectSessionType';
import SelectDuration from './BookCalendar/SelectDuration';
import Calendar from './BookCalendar/Calendar';
import SelectTime from './BookCalendar/SelectTime';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import BackToTopButton from '@/shared/components/BackToTopButton';
import { green, lightGreen } from '@/shared/styles/colors';
import { BodyText } from '@/shared/components/StyledText';
import { TouchableOpacity, useWindowDimensions } from 'react-native';
import Button from '@/shared/components/Button';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_AVAILABILITY_SLOTS } from '@/shared/queries';
import { SEND_BOOKING_REQUEST_NOTIFICATION } from '@/shared/mutations';
import { EventType, SessionType, Stylist } from '@/shared/types/generated';
import {
  MutationResult,
  MutationSendBookingRequestNotificationArgs,
  QueryResult,
  QueryAvailabilitySlotsArgs,
} from '@/shared/types/generated';
import { format } from 'date-fns';
import { useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import { useEventConfiguration } from '@/shared/hooks/useEventConfiguration';
import { ActivityIndicator } from 'react-native-paper';
import SentryService from '@/services/sentry/Sentry.service';
import { useGetUser } from '@/shared/hooks/useGetUser';
import useSendbirdChannelUrl from '@/shared/hooks/useSendbirdChannelUrl';
import { useChannelRouting } from '@/shared/hooks/useChannelRouting';

export interface AvailableTime {
  value: Date;
  label: string;
}

/**
 * Check if a specific slot date falls within the event date range
 * @param slotDate - Date string in YYYY-MM-DD format
 * @param eventStartDate - Event start date in YYYY-MM-DD format
 * @param eventEndDate - Event end date in YYYY-MM-DD format
 * @returns true if slot date is within event range, false otherwise
 */
const isSlotWithinEventDateRange = (
  slotDate: string,
  eventStartDate: string,
  eventEndDate: string,
): boolean => {
  try {
    const slot = new Date(slotDate);
    const start = new Date(eventStartDate);
    const end = new Date(eventEndDate);

    return slot >= start && slot <= end;
  } catch (error) {
    return false;
  }
};

/**
 * Apply filtering logic to availability slots based on session type
 * @param slots - All available time slots
 * @param sessionType - The type of session being booked
 * @param eventPeriods - Event date ranges (only applies to IN_PERSON_EVENT)
 * @param stylist - The stylist being booked
 * @returns filtered array of available times
 */
const filterSlotsBySessionType = (
  slots: AvailableTime[],
  sessionType: SessionType,
  eventPeriods: { startDate: string; endDate: string }[],
  stylist: Stylist | null,
): AvailableTime[] => {
  // For regular in-person sessions (at client location), no filtering needed
  // The availability is based on stylist's open hours
  if (sessionType === SessionType.InPersonSession) {
    return slots;
  }

  // For virtual sessions
  if (sessionType === SessionType.Video) {
    if (!stylist?.isEventAttendee) {
      return slots;
    }
    // Exclude slots during event periods if stylist is attending an event
    return slots.filter((slot) => {
      const slotDateString = format(slot.value, 'yyyy-MM-dd');
      const isWithinEventPeriod = eventPeriods.some((period) =>
        isSlotWithinEventDateRange(
          slotDateString,
          period.startDate,
          period.endDate,
        ),
      );
      return !isWithinEventPeriod;
    });
  }

  // For IN_PERSON_EVENT sessions, only show slots within event periods
  if (sessionType === SessionType.InPersonEvent) {
    return slots.filter((slot) => {
      const slotDateString = format(slot.value, 'yyyy-MM-dd');
      const isWithinEventPeriod = eventPeriods.some((period) =>
        isSlotWithinEventDateRange(
          slotDateString,
          period.startDate,
          period.endDate,
        ),
      );
      return isWithinEventPeriod;
    });
  }

  return slots;
};

interface BookCalendarProps {
  onDismissModal?: () => void;
}

const BookCalendar = ({ onDismissModal }: BookCalendarProps) => {
  const { selectedStylist, booking } = useExploreStore();
  const setSnackbarState = useStore((state) => state.setSnackbarState);
  const [availableTimes, setAvailableTimes] = useState<AvailableTime[]>([]);
  const [allAvailableTimes, setAllAvailableTimes] = useState<AvailableTime[]>(
    [],
  );
  const [availabilityStatus, setAvailabilityStatus] = useState<
    'loading' | 'ready' | 'error'
  >('loading');
  const [hasAnyAvailability, setHasAnyAvailability] = useState(false);
  const router = useRouter();
  const { width } = useWindowDimensions();
  const { user } = useGetUser();
  const { fetchChannelUrl } = useSendbirdChannelUrl();
  const { navigateToChannel } = useChannelRouting();
  const scrollToTop = useScrollToTop();

  const [sendBookingRequestNotification] = useMutation<
    MutationResult<'sendBookingRequestNotification'>,
    MutationSendBookingRequestNotificationArgs
  >(SEND_BOOKING_REQUEST_NOTIFICATION);

  // Derive in-person session state from booking
  const isInPersonSession =
    booking.sessionType === SessionType.InPersonSession ||
    booking.sessionType === SessionType.InPersonEvent;

  // Get event periods for slot filtering (used by Video sessions with event attendees and InPersonEvent sessions)
  const { eventPeriods } = useEventConfiguration();

  const [getAvailabilitySlots, { loading }] = useLazyQuery<
    QueryResult<'availabilitySlots'>,
    QueryAvailabilitySlotsArgs
  >(GET_AVAILABILITY_SLOTS, {
    fetchPolicy: 'no-cache',
    onError: (error) => {
      SentryService.captureError(
        `Error fetching availability slots for stylist ${selectedStylist?.id}`,
        error,
      );
      setSnackbarState({
        open: true,
        message: 'Unable to load time slots. Please try again.',
        severity: 'error',
      });
    },
    onCompleted: (data) => {
      const allSlots = data.availabilitySlots
        .map((slot) => {
          if (!slot?.startTime) {
            return null;
          }
          const dateCorrected = new Date(parseInt(slot.startTime) * 1000);
          return {
            value: dateCorrected,
            label: format(dateCorrected, 'h:mm a'),
          };
        })
        .filter((slot) => slot !== null) as AvailableTime[];

      // Store all unfiltered slots
      setAllAvailableTimes(allSlots);

      // Apply filtering based on session type
      const filteredSlots = filterSlotsBySessionType(
        allSlots,
        booking.sessionType,
        eventPeriods,
        selectedStylist,
      );
      setAvailableTimes(filteredSlots);
    },
  });

  // Re-filter slots when session type or event data changes
  useEffect(() => {
    if (allAvailableTimes.length > 0) {
      const filteredSlots = filterSlotsBySessionType(
        allAvailableTimes,
        booking.sessionType,
        eventPeriods,
        selectedStylist,
      );
      setAvailableTimes(filteredSlots);
    }
  }, [booking.sessionType, allAvailableTimes, eventPeriods, selectedStylist]);

  // Baseline query: check if the stylist has any availability at all using
  // the smallest slot size. If even 30-min slots return empty, no duration
  // will have availability (no calendar setup).
  const [checkBaselineAvailability] = useLazyQuery<
    QueryResult<'availabilitySlots'>,
    QueryAvailabilitySlotsArgs
  >(GET_AVAILABILITY_SLOTS, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setHasAnyAvailability(data.availabilitySlots.length > 0);
      setAvailabilityStatus('ready');
    },
    onError: (error) => {
      SentryService.captureError(
        `Error checking baseline availability for stylist ${selectedStylist?.id}`,
        error,
      );
      setAvailabilityStatus('error');
    },
  });

  const fetchBaselineAvailability = useCallback(() => {
    if (!selectedStylist?.id) {
      return;
    }
    setAvailabilityStatus('loading');
    checkBaselineAvailability({
      variables: {
        input: {
          stylistId: selectedStylist.id,
          eventType: EventType.ThirtyMinutes,
          currentDate: new Date().toISOString(),
        },
      },
    });
  }, [selectedStylist?.id, checkBaselineAvailability]);

  useEffect(() => {
    fetchBaselineAvailability();
  }, [fetchBaselineAvailability]);

  /**
   * Map session duration to the appropriate EventType for availability query
   */
  const getEventTypeForDuration = (
    duration: number,
    isInPerson: boolean,
  ): EventType => {
    if (!isInPerson) {
      return duration === 30 ? EventType.ThirtyMinutes : EventType.SixtyMinutes;
    }

    // Map in-person durations (in minutes) to EventType
    switch (duration) {
      case 120:
        return EventType.InPerson_2Hour;
      case 180:
        return EventType.InPerson_3Hour;
      case 240:
        return EventType.InPerson_4Hour;
      case 300:
        return EventType.InPerson_5Hour;
      case 360:
        return EventType.InPerson_6Hour;
      case 420:
        return EventType.InPerson_7Hour;
      case 480:
        return EventType.InPerson_8Hour;
      default:
        // Default to 2 hour for in-person if duration doesn't match
        return EventType.InPerson_2Hour;
    }
  };

  useEffect(() => {
    if (!selectedStylist?.id) {
      return;
    }
    const eventType = getEventTypeForDuration(
      booking.sessionDuration,
      isInPersonSession,
    );
    getAvailabilitySlots({
      variables: {
        input: {
          stylistId: selectedStylist.id,
          eventType,
          currentDate: new Date().toISOString(),
        },
      },
    });
  }, [
    booking.sessionDuration,
    selectedStylist?.id,
    getAvailabilitySlots,
    isInPersonSession,
  ]);

  const isLoading = availabilityStatus === 'loading';
  const isError = availabilityStatus === 'error';
  const hasNoAvailability =
    availabilityStatus === 'ready' && !hasAnyAvailability;

  const handleMessageStylist = async () => {
    if (!user?.id || !selectedStylist?.userId) {
      return;
    }
    try {
      const channelUrl = await fetchChannelUrl(selectedStylist.userId, user.id);

      if (channelUrl) {
        try {
          await sendBookingRequestNotification({
            variables: { input: { stylistId: selectedStylist.id } },
          });
        } catch (notificationError) {
          SentryService.captureError(
            `Failed to send booking request notification for stylist ${selectedStylist?.id}`,
            notificationError,
          );
        }
        onDismissModal?.();
        navigateToChannel(channelUrl);
      }
    } catch (error) {
      SentryService.captureError(
        `Error opening message channel with stylist ${selectedStylist?.id}`,
        error,
      );
      setSnackbarState({
        open: true,
        message: 'Unable to open messages. Please try again later.',
        severity: 'error',
      });
    }
  };

  const handleHelpCenterPress = () => {
    onDismissModal?.();
    router.push(ROUTES.HELP.path);
  };

  if (isLoading) {
    return (
      <Container style={{ width }}>
        <LoadingContainer>
          <ActivityIndicator size="large" color={green} />
        </LoadingContainer>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container>
        <ErrorContainer>
          <ErrorText>
            Something went wrong loading availability. Please try again.
          </ErrorText>
          <Button
            text="Retry"
            onPress={fetchBaselineAvailability}
            variant="primary"
            customStyle={{ width: 160, height: 48 }}
          />
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Content
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          alignItems: 'center',
          // Setting explicit width to prevent issue where content width changes size once data loads in
          width: width,
          ...(hasNoAvailability && { flexGrow: 1, justifyContent: 'center' }),
        }}
      >
        <PaddingContainer marginBottom={hasNoAvailability ? 12 : 32}>
          <Title>
            Book a Styling Session With {selectedStylist?.user?.firstName}
          </Title>
          {!hasNoAvailability && (
            <Subtitle>Select a time that works for you</Subtitle>
          )}
        </PaddingContainer>

        {hasNoAvailability ? (
          <NoAvailabilityContainer>
            <NoAvailabilityText>
              This stylist currently has no availability. Message them with your
              preferred date and time to see if they can accommodate.
            </NoAvailabilityText>
            <Button
              text="Send Message"
              onPress={handleMessageStylist}
              variant="primary"
              customStyle={{ width: 233, height: 48 }}
            />
          </NoAvailabilityContainer>
        ) : (
          <>
            <SelectSessionType />
            <SelectDuration />
            <Calendar availableTimes={availableTimes} loading={loading} />
            <SelectTime loading={loading} availableTimes={availableTimes} />
            <Spacer />

            {!loading && (
              <>
                <MessagePromptContainer>
                  <MessagePromptText>
                    Don&apos;t see a time that works? Message the stylist to see
                    if they can accommodate.
                  </MessagePromptText>
                  <Button
                    text="Send Message"
                    onPress={handleMessageStylist}
                    variant="primary"
                    customStyle={{ width: 233, height: 48 }}
                  />
                </MessagePromptContainer>
                <Spacer />
              </>
            )}

            <QuestionsText>Have questions or need help?</QuestionsText>
            <TouchableOpacity onPress={handleHelpCenterPress}>
              <QuestionsLink>Visit Help Center</QuestionsLink>
            </TouchableOpacity>

            <Spacer height={54} />

            <BackToTopButton scrollToTop={scrollToTop} />
            <Spacer height={54} />
          </>
        )}
      </Content>
    </Container>
  );
};

const Container = styled.View({
  flex: 1,
});

const Content = styled(ScrollableView)({
  flex: 1,
});

const Spacer = styled.View(({ height = 47 }: { height?: number }) => ({
  minHeight: height,
  width: '100%',
}));

const QuestionsText = styled(BodyText)({
  fontSize: 14,
  color: lightGreen,
  lineHeight: 16,
});

const QuestionsLink = styled(QuestionsText)({
  textDecorationLine: 'underline',
  fontWeight: 500,
  color: green,
});

const MessagePromptContainer = styled.View({
  alignItems: 'center',
  paddingHorizontal: 32,
  gap: 16,
});

const MessagePromptText = styled(BodyText)({
  fontSize: 16,
  lineHeight: 20,
  color: lightGreen,
  textAlign: 'center',
  marginBottom: 4,
});

const LoadingContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const ErrorContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  paddingHorizontal: 32,
  gap: 24,
});

const ErrorText = styled(BodyText)({
  fontSize: 16,
  lineHeight: 20,
  color: green,
  textAlign: 'center',
});

const NoAvailabilityContainer = styled.View({
  alignItems: 'center',
  paddingHorizontal: 32,
  gap: 24,
});

const NoAvailabilityText = styled(BodyText)({
  fontSize: 16,
  lineHeight: 20,
  color: green,
  textAlign: 'center',
});

export default BookCalendar;
